import {
    Cancel,
    Description,
    ExpandLess,
    ExpandMore,
} from '@mui/icons-material'
import {
    Box,
    Button,
    Chip,
    Collapse,
    LinearProgress,
    Link,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@apollo/client'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    DismissBulkUploadProcess,
    GetBulkUploadProcesses,
    GetLatestErrorDownload,
    RetryBulkUpload,
} from 'recruiter/graphql/candidateBulkUpload'
import { recruiterRouteConfig } from 'recruiter/routes'
import { formatRelativeTime } from 'shared/utils/dateFormatter'
import { GetBulkUploadProcessesQuery } from '__generated__/graphql'

type Process = GetBulkUploadProcessesQuery['userProcesses'][number]

type UploadStatus =
    | 'processing'
    | 'completed'
    | 'completed_with_errors'
    | 'failed'

function deriveStatus(process: Process): UploadStatus {
    const eventType = process.latestEvent?.type
    if (
        !eventType ||
        eventType === 'started' ||
        eventType === 'validating' ||
        eventType === 'processing' ||
        eventType === 'creating_roster'
    ) {
        return 'processing'
    }
    if (eventType === 'error') return 'failed'
    if (eventType === 'success') {
        const failed = process.latestEvent?.metadata?.failed ?? 0
        return failed > 0 ? 'completed_with_errors' : 'completed'
    }
    return 'processing'
}

const statusConfig: Record<
    UploadStatus,
    { label: string; color: string; bg: string; dotColor: string }
> = {
    processing: {
        label: 'PROCESSING',
        color: '#1565c0',
        bg: '#e3f2fd',
        dotColor: '#1976d2',
    },
    completed: {
        label: 'COMPLETED',
        color: '#2e7d32',
        bg: '#e8f5e9',
        dotColor: '#43a047',
    },
    completed_with_errors: {
        label: 'COMPLETED WITH ERRORS',
        color: '#b26a00',
        bg: '#fff4e5',
        dotColor: '#f9a825',
    },
    failed: {
        label: 'FAILED',
        color: '#c62828',
        bg: '#fdecea',
        dotColor: '#d32f2f',
    },
}

type FilterValue = 'all' | UploadStatus

function UploadRow({ process }: { process: Process }) {
    const [errorsExpanded, setErrorsExpanded] = useState(false)
    const status = deriveStatus(process)
    const config = statusConfig[status]
    const metadata = process.latestEvent?.metadata
    const payload = process.payload as Record<string, unknown> | null
    const fileName = (payload?.fileName as string) ?? 'Untitled upload'

    const { data: errorDownload } = useQuery(GetLatestErrorDownload, {
        fetchPolicy: 'network-only',
        skip: status !== 'completed_with_errors',
    })

    const navigate = useNavigate()

    const [retry, { loading: retrying }] = useMutation(RetryBulkUpload, {
        variables: { processId: process.id },
        refetchQueries: [GetBulkUploadProcesses],
    })

    const [dismiss, { loading: dismissing }] = useMutation(
        DismissBulkUploadProcess,
        {
            variables: { processId: process.id },
            refetchQueries: [GetBulkUploadProcesses],
        }
    )

    const handleDownloadErrorReport = () => {
        const url = errorDownload?.latestErrorDownload?.url
        if (!url) return
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.click()
        link.remove()
    }

    const renderSubtitle = () => {
        if (status === 'failed') {
            const message = (metadata?.message as string) ?? 'Upload failed'
            return (
                <Typography variant="caption" sx={{ color: '#c62828' }}>
                    {message}
                </Typography>
            )
        }
        if (status === 'completed' || status === 'completed_with_errors') {
            const imported = metadata?.imported ?? 0
            const failed = metadata?.failed ?? 0
            const parts: string[] = [`${imported} imported`]
            if (failed > 0) parts.push(`${failed} errors`)
            parts.push(
                formatRelativeTime(new Date(process.latestEvent!.createdAt))
            )
            return (
                <Typography variant="caption" color="text.secondary">
                    {parts.join(' · ')}
                </Typography>
            )
        }
        const totalRows = metadata?.totalRows
        const eventType = process.latestEvent?.type
        const parts: string[] = []
        if (totalRows) parts.push(`${totalRows} candidates`)
        if (eventType === 'creating_roster') {
            parts.push('creating search')
        }
        parts.push(formatRelativeTime(new Date(process.createdAt)))
        return (
            <Typography variant="caption" color="text.secondary">
                {parts.join(' · ')}
            </Typography>
        )
    }

    const renderProgress = () => {
        if (status !== 'processing') return null
        const current = metadata?.current as number | undefined
        const total = metadata?.total as number | undefined
        if (current != null && total != null && total > 0) {
            return (
                <Box sx={{ mt: 1.25 }}>
                    <LinearProgress
                        variant="determinate"
                        value={(current / total) * 100}
                        sx={{ height: 3, borderRadius: 1 }}
                    />
                </Box>
            )
        }
        return (
            <Box sx={{ mt: 1.25 }}>
                <LinearProgress sx={{ height: 3, borderRadius: 1 }} />
            </Box>
        )
    }

    const renderActions = () => {
        if (status === 'processing') {
            const current = metadata?.current as number | undefined
            const total = metadata?.total as number | undefined
            if (current != null && total != null) {
                return (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {current} / {total}
                    </Typography>
                )
            }
            return null
        }
        if (status === 'completed_with_errors') {
            return (
                <Stack direction="row" spacing={1.5}>
                    <Link
                        component="button"
                        variant="caption"
                        fontWeight={500}
                        sx={{ letterSpacing: 0.5, cursor: 'pointer' }}
                        onClick={() => setErrorsExpanded(!errorsExpanded)}
                    >
                        {errorsExpanded ? 'HIDE ERRORS' : 'SHOW ERRORS'}
                        {errorsExpanded ? (
                            <ExpandLess
                                sx={{
                                    fontSize: 14,
                                    ml: 0.25,
                                    verticalAlign: 'middle',
                                }}
                            />
                        ) : (
                            <ExpandMore
                                sx={{
                                    fontSize: 14,
                                    ml: 0.25,
                                    verticalAlign: 'middle',
                                }}
                            />
                        )}
                    </Link>
                </Stack>
            )
        }
        if (status === 'failed') {
            return (
                <Stack direction="row" spacing={1.5}>
                    <Button
                        size="small"
                        variant="text"
                        disabled={retrying}
                        onClick={() => retry()}
                        sx={{
                            minWidth: 0,
                            fontSize: 12,
                            fontWeight: 500,
                            letterSpacing: 0.5,
                            p: 0,
                        }}
                    >
                        {retrying ? 'RETRYING…' : 'RETRY'}
                    </Button>
                    <Button
                        size="small"
                        variant="text"
                        disabled={dismissing}
                        onClick={() => dismiss()}
                        sx={{
                            minWidth: 0,
                            fontSize: 12,
                            fontWeight: 500,
                            letterSpacing: 0.5,
                            p: 0,
                            color: 'text.secondary',
                        }}
                    >
                        {dismissing ? 'DISMISSING…' : 'DISMISS'}
                    </Button>
                </Stack>
            )
        }
        const rosterId = metadata?.rosterId as string | undefined
        if (!rosterId) return null
        return (
            <Button
                size="small"
                variant="text"
                onClick={() =>
                    navigate(`${recruiterRouteConfig.rosters.path}/${rosterId}`)
                }
                sx={{
                    minWidth: 0,
                    fontSize: 12,
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    p: 0,
                }}
            >
                VIEW
            </Button>
        )
    }

    const iconBg =
        status === 'failed'
            ? '#fdecea'
            : status === 'processing'
              ? '#e3f2fd'
              : '#f1f3f4'
    const iconColor =
        status === 'failed'
            ? '#c62828'
            : status === 'processing'
              ? '#1976d2'
              : 'rgba(0,0,0,0.54)'

    return (
        <Box
            sx={{
                p: '14px 16px',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                '&:last-child': { borderBottom: 'none' },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        bgcolor: iconBg,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    {status === 'failed' ? (
                        <Cancel sx={{ fontSize: 16, color: iconColor }} />
                    ) : (
                        <Description sx={{ fontSize: 16, color: iconColor }} />
                    )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 0.25 }}
                    >
                        <Typography variant="body2" fontWeight={500} noWrap>
                            {fileName}
                        </Typography>
                        <Chip
                            size="small"
                            label={config.label}
                            sx={{
                                height: 20,
                                fontSize: 11,
                                fontWeight: 500,
                                letterSpacing: 0.3,
                                bgcolor: config.bg,
                                color: config.color,
                                '& .MuiChip-label': { px: 1 },
                                '&::before': {
                                    content: '""',
                                    display: 'inline-block',
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: config.dotColor,
                                    ml: 1,
                                    mr: -0.5,
                                },
                            }}
                        />
                    </Stack>
                    {renderSubtitle()}
                </Box>
                {renderActions()}
            </Box>
            {renderProgress()}
            <Collapse in={errorsExpanded}>
                <Box
                    sx={{
                        mt: 1.5,
                        ml: '44px',
                        bgcolor: '#fff8e1',
                        borderLeft: '3px solid #f9a825',
                        borderRadius: '0 4px 4px 0',
                        p: '12px 14px',
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                    >
                        <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{ color: '#5d4037' }}
                        >
                            {metadata?.failed ?? 0} rows could not be imported
                        </Typography>
                        {errorDownload?.latestErrorDownload?.url && (
                            <Link
                                component="button"
                                variant="caption"
                                fontWeight={500}
                                sx={{
                                    letterSpacing: 0.4,
                                    cursor: 'pointer',
                                    fontSize: 11,
                                }}
                                onClick={handleDownloadErrorReport}
                            >
                                DOWNLOAD ERROR REPORT
                            </Link>
                        )}
                    </Stack>
                </Box>
            </Collapse>
        </Box>
    )
}

const RecentUploads: React.FC = () => {
    const { data } = useQuery(GetBulkUploadProcesses, {
        fetchPolicy: 'network-only',
        pollInterval: 5000,
    })

    const [statusFilter, setStatusFilter] = useState<FilterValue>('all')

    const processes = data?.userProcesses ?? []
    const filtered =
        statusFilter === 'all'
            ? processes
            : processes.filter((p) => deriveStatus(p) === statusFilter)

    if (processes.length === 0) return null

    return (
        <Box sx={{ mt: 3.5 }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                sx={{ mb: 1.5 }}
            >
                <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography
                        variant="subtitle1"
                        fontWeight={500}
                        sx={{
                            fontFamily: "'Lora', 'Georgia', serif",
                            fontSize: 16,
                        }}
                    >
                        Recent uploads
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Last 30 days
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Select
                        size="small"
                        value={statusFilter}
                        onChange={(e) =>
                            setStatusFilter(e.target.value as FilterValue)
                        }
                        sx={{
                            fontSize: 12,
                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                        }}
                    >
                        <MenuItem value="all">All statuses</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="completed_with_errors">
                            With errors
                        </MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                </Stack>
            </Stack>
            <Box
                sx={{
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                {filtered.map((process) => (
                    <UploadRow key={process.id} process={process} />
                ))}
                {filtered.length === 0 && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            No uploads match this filter
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default RecentUploads
